import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  ShoppingCart, 
  Trash2, 
  Cloud, 
  Globe, 
  ArrowRight, 
  ArrowLeft,
  Loader2, 
  CreditCard, 
  HardDrive, 
  Users, 
  Shield, 
  Zap,
  CheckCircle2,
  ExternalLink,
  Scale,
  AlertCircle,
  Phone,
  Mail,
  User,
  Building,
  MapPin,
  Lock,
  Tag,
  XCircle,
  Ticket
} from 'lucide-react';

const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

const CartCheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, removeCloud, removeDomain, getCartTotals, getItemCount } = useCart();
  const { user, token, login, register } = useAuth();
  
  // Current step: 'cart' | 'account' | 'payment'
  const [currentStep, setCurrentStep] = useState('cart');
  const [processing, setProcessing] = useState(false);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  
  // Login form
  const [isLoginMode, setIsLoginMode] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  
  // Registration form - vollständig wie auf CheckoutPage
  const [regForm, setRegForm] = useState({
    email: '',
    password: '',
    passwordConfirm: '',
    firstName: '',
    lastName: '',
    company: '',
    street: '',
    zipCode: '',
    city: '',
    phone: '',
  });
  
  // Legal checkboxes
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [slaAccepted, setSlaAccepted] = useState(false);
  
  // Payment method - default stripe
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  
  // Existing customer data (wenn eingeloggt)
  const [existingCustomer, setExistingCustomer] = useState(null);
  
  // Flag: Redirect zu Zahlungsanbieter läuft (verhindert useEffect-Navigation)
  const isRedirecting = useRef(false);

  const totals = getCartTotals();
  const itemCount = getItemCount();
  
  // Check if cart has domains (for phone requirement)
  const hasDomains = cart.domains?.length > 0 || (cart.cloud?.domain?.type === 'purchase');

  // Calculate totals with coupon discount
  const getDiscountedTotals = () => {
    if (!appliedCoupon) {
      return {
        ...totals,
        discount: 0,
        finalTotal: totals.initialTotal
      };
    }
    
    let discountableAmount = totals.initialTotal;
    
    // If coupon doesn't apply to domains, exclude domain costs
    if (appliedCoupon.applies_to_domain === false) {
      const domainCosts = cart.domains?.reduce((sum, d) => sum + (d.price || 0), 0) || 0;
      discountableAmount = totals.initialTotal - domainCosts;
    }
    
    let discountAmount = 0;
    if (appliedCoupon.discount_type === 'percent') {
      discountAmount = discountableAmount * (appliedCoupon.discount_value / 100);
    } else {
      discountAmount = Math.min(appliedCoupon.discount_value, discountableAmount);
    }
    
    return {
      ...totals,
      discount: discountAmount,
      finalTotal: Math.max(0, totals.initialTotal - discountAmount)
    };
  };

  const discountedTotals = getDiscountedTotals();

  // Coupon validation
  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Bitte gib einen Gutscheincode ein');
      return;
    }
    
    setCouponLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/coupons/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: couponCode.trim(),
          order_value: totals.initialTotal
        })
      });
      
      const data = await response.json();
      
      if (data.valid) {
        setAppliedCoupon({
          code: data.code,
          discount_type: data.discount_type,
          discount_value: data.discount_value,
          discount_amount: data.discount_amount,
          duration: data.duration || 'once',
          duration_months: data.duration_months || 0,
          applies_to: data.applies_to || 'all',
          applies_to_domain: data.applies_to_domain,
          message: data.message
        });
        toast.success(`Gutschein "${data.code}" eingelöst!`);
      } else {
        toast.error(data.message || 'Ungültiger Gutscheincode');
        setAppliedCoupon(null);
      }
    } catch (error) {
      console.error('Coupon validation error:', error);
      toast.error('Fehler bei der Gutschein-Prüfung');
    } finally {
      setCouponLoading(false);
    }
  };

  // Helper to get duration label
  const getDurationLabel = (coupon) => {
    if (!coupon) return '';
    switch (coupon.duration) {
      case 'once': return 'einmalig';
      case 'recurring': return 'wiederkehrend';
      case 'months': return `für ${coupon.duration_months} Monate`;
      case 'first_year': return 'erstes Jahr';
      default: return '';
    }
  };

  // Remove coupon
  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    toast.info('Gutschein entfernt');
  };

  // Load existing customer data if logged in
  useEffect(() => {
    const loadCustomerData = async () => {
      const authToken = token || localStorage.getItem('auth_token');
      if (!authToken) return;
      
      setLoadingCustomer(true);
      try {
        const res = await fetch(`${backendUrl}/api/customers/me`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (res.ok) {
          const data = await res.json();
          setExistingCustomer(data);
          // Pre-fill form with existing data
          setRegForm(prev => ({
            ...prev,
            firstName: data.first_name || '',
            lastName: data.last_name || '',
            company: data.company || '',
            street: data.street || '',
            zipCode: data.zip_code || '',
            city: data.city || '',
            phone: data.phone || '',
            email: data.email || user?.email || ''
          }));
        }
      } catch (e) {
        console.error('Error loading customer:', e);
      } finally {
        setLoadingCustomer(false);
      }
    };
    
    if (user || localStorage.getItem('auth_token')) {
      loadCustomerData();
    }
  }, [user, token]);

  // Redirect to cart step if cart is empty (but not during payment redirect)
  useEffect(() => {
    if (isRedirecting.current) return;
    if (itemCount === 0 && currentStep !== 'cart') {
      setCurrentStep('cart');
    }
  }, [itemCount, currentStep]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(price || 0);
  };

  const isAuthenticated = !!(user || localStorage.getItem('auth_token'));

  // Validation
  const canProceedFromAccount = () => {
    if (isAuthenticated) {
      // Nur Adresse und ggf. Telefon prüfen
      if (!regForm.firstName || !regForm.lastName || !regForm.street || !regForm.zipCode || !regForm.city) {
        return false;
      }
      if (hasDomains && !regForm.phone) {
        return false;
      }
      return true;
    } else {
      // Neuer Benutzer - alles prüfen
      if (!regForm.email || !regForm.password || !regForm.firstName || !regForm.lastName) {
        return false;
      }
      if (!regForm.street || !regForm.zipCode || !regForm.city) {
        return false;
      }
      if (regForm.password !== regForm.passwordConfirm) {
        return false;
      }
      if (regForm.password.length < 8) {
        return false;
      }
      if (hasDomains && !regForm.phone) {
        return false;
      }
      return true;
    }
  };

  // Handle login - ohne page reload, bleibt auf dem Account-Step
  const handleLogin = async () => {
    if (!loginForm.email || !loginForm.password) {
      toast.error('Bitte E-Mail und Passwort eingeben');
      return;
    }
    
    setProcessing(true);
    try {
      // AuthContext login setzt token + user automatisch
      const data = await login(loginForm.email, loginForm.password);
      const authToken = data.access_token;
      
      toast.success('Erfolgreich angemeldet!');
      setIsLoginMode(false);
      
      // Kundendaten laden und Formular vorausfüllen
      setLoadingCustomer(true);
      try {
        const res = await fetch(`${backendUrl}/api/customers/me`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (res.ok) {
          const customerData = await res.json();
          setExistingCustomer(customerData);
          setRegForm(prev => ({
            ...prev,
            firstName: customerData.first_name || '',
            lastName: customerData.last_name || '',
            company: customerData.company || '',
            street: customerData.street || '',
            zipCode: customerData.zip_code || '',
            city: customerData.city || '',
            phone: customerData.phone || '',
            email: customerData.email || loginForm.email
          }));
        }
      } catch (e) {
        console.error('Error loading customer after login:', e);
      } finally {
        setLoadingCustomer(false);
      }
    } catch (e) {
      toast.error(e.message || 'Anmeldung fehlgeschlagen');
    } finally {
      setProcessing(false);
    }
  };

  // Handle registration + direkt zur Zahlung
  const handleRegisterAndProceed = async () => {
    if (!canProceedFromAccount()) {
      toast.error('Bitte fülle alle Pflichtfelder aus');
      return;
    }
    
    setProcessing(true);
    try {
      // 1. Registrierung über AuthContext (setzt token + user automatisch)
      const data = await register(
        regForm.email, 
        regForm.password, 
        `${regForm.firstName} ${regForm.lastName}`.trim()
      );
      const authToken = data.access_token;
      
      // 2. Kundendaten mit Adresse aktualisieren (via PUT, nicht POST - Customer existiert bereits)
      const updateRes = await fetch(`${backendUrl}/api/customers/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          first_name: regForm.firstName,
          last_name: regForm.lastName,
          company: regForm.company || null,
          street: regForm.street,
          zip_code: regForm.zipCode,
          city: regForm.city,
          phone: regForm.phone || null
        })
      });
      
      if (!updateRes.ok) {
        console.error('Customer update failed:', await updateRes.text());
      }
      
      toast.success('Konto erfolgreich erstellt!');
      
      // 3. Direkt zur Zahlung
      await initiatePayment(authToken);
      
    } catch (e) {
      toast.error(e.message || 'Registrierung fehlgeschlagen');
    } finally {
      setProcessing(false);
    }
  };

  // Update customer data (für eingeloggte Benutzer)
  const handleUpdateAndProceed = async () => {
    if (!canProceedFromAccount()) {
      toast.error('Bitte fülle alle Pflichtfelder aus');
      return;
    }
    
    const authToken = token || localStorage.getItem('auth_token');
    if (!authToken) {
      toast.error('Nicht angemeldet');
      return;
    }
    
    setProcessing(true);
    try {
      // Update customer profile
      await fetch(`${backendUrl}/api/customers/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          first_name: regForm.firstName,
          last_name: regForm.lastName,
          company: regForm.company || null,
          street: regForm.street,
          zip_code: regForm.zipCode,
          city: regForm.city,
          phone: regForm.phone || null
        })
      });
      
      // Direkt zur Zahlung weiterleiten
      await initiatePayment(authToken);
      
    } catch (e) {
      console.error('Update error:', e);
      toast.error('Fehler beim Aktualisieren der Daten');
    } finally {
      setProcessing(false);
    }
  };

  // Initiate payment - erstellt Bestellung und leitet zur Zahlung weiter
  const initiatePayment = async (authToken) => {
    try {
      // 1. Customer-ID holen
      const customerRes = await fetch(`${backendUrl}/api/customers/me`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (!customerRes.ok) {
        toast.error('Kundendaten konnten nicht geladen werden');
        return;
      }
      
      const customerData = await customerRes.json();
      const customerId = customerData.id;
      
      if (!customerId) {
        toast.error('Kein Kundenkonto gefunden. Bitte melde dich erneut an.');
        return;
      }
      
      // 2. Cloud-Bestellung erstellen (inkl. verknüpfter Domain)
      if (cart.cloud) {
        const cloudDomain = cart.cloud.domain;
        const formDataStored = cart.cloud.formData || {};
        const domainUsage = formDataStored.domainUsage || {};
        
        // Plan-Daten korrekt mappen (inkl. cloud_subdomain und dns_record_type)
        const planData = {
          storage_gb: cart.cloud.storage || 250,
          collabora_users: cart.cloud.collabora_users || 0,
          antivirus: cart.cloud.antivirus || false,
          hp_backend: cart.cloud.hp_backend || false,
          custom_domain: cloudDomain?.name || null,
          domain_type: cloudDomain?.type || 'subdomain',
          domain_price: cloudDomain?.type === 'purchase' ? (cloudDomain?.price || null) : null,
          cloud_url: cloudDomain?.name || null
        };
        
        // Für Kaufdomains: Subdomain und DNS-Einstellungen aus dem Konfigurator übernehmen
        if (cloudDomain?.type === 'purchase' && domainUsage.useSubdomain && domainUsage.subdomain) {
          planData.cloud_subdomain = domainUsage.subdomain;
          planData.dns_record_type = domainUsage.recordType || 'CNAME';
          planData.cloud_url = `${domainUsage.subdomain}.${cloudDomain.name}`;
        }
        
        const orderRes = await fetch(`${backendUrl}/api/orders`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customer_id: customerId,
            plan: planData,
            payment_method: paymentMethod,
            coupon_code: appliedCoupon?.code || null
          })
        });
        
        if (!orderRes.ok) {
          const err = await orderRes.json().catch(() => ({}));
          toast.error(err.detail || 'Bestellung konnte nicht erstellt werden');
          return;
        }
        
        const order = await orderRes.json();
        
        // Zahlung initiieren
        const payRes = await fetch(`${backendUrl}/api/payments/initiate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            order_id: order.id,
            payment_method: paymentMethod,
            origin_url: window.location.origin
          })
        });
        
        if (!payRes.ok) {
          const payErr = await payRes.json().catch(() => ({}));
          toast.error(payErr.detail || 'Zahlung konnte nicht gestartet werden');
          return;
        }
        
        const payData = await payRes.json();
        
        if (payData.is_free) {
          toast.success('Deine Bestellung ist kostenlos! Du wirst weitergeleitet...');
        } else {
          toast.success('Du wirst zur Zahlung weitergeleitet...');
        }
        
        // Sicherer Redirect: Flag setzen (Cart wird erst NACH erfolgreicher Zahlung geleert - BUG-11)
        isRedirecting.current = true;
        window.location.href = payData.payment_url;
        return;
      }
      
      // 3. Domain-Only Bestellungen (ohne linkedToCloud-Domains)
      const standaloneDomains = (cart.domains || []).filter(d => !d.linkedToCloud);
      
      if (standaloneDomains.length > 0) {
        const firstDomain = standaloneDomains[0];
        const isTransfer = firstDomain.type === 'transfer';
        
        // Hinweis bei mehreren Domains
        if (standaloneDomains.length > 1) {
          toast.info(`${standaloneDomains.length} Domains im Warenkorb. Die erste wird jetzt bestellt, weitere kannst du danach bestellen.`);
        }
        
        const endpoint = isTransfer ? '/api/domain-transfers' : '/api/domain-orders';
        const body = isTransfer 
          ? { domain: firstDomain.domain, auth_code: firstDomain.authCode }
          : { domain: firstDomain.domain, years: firstDomain.years || 1 };
        
        const res = await fetch(`${backendUrl}${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify(body)
        });
        
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          toast.error(err.detail || 'Bestellung fehlgeschlagen');
          return;
        }
        
        const order = await res.json();
        const orderId = order.order_id || order.id;
        
        // Zahlung initiieren
        const payEndpoint = isTransfer 
          ? `/api/domain-transfers/${orderId}/pay`
          : `/api/domain-orders/${orderId}/pay`;
        
        const payRes = await fetch(`${backendUrl}${payEndpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            payment_method: paymentMethod,
            origin_url: window.location.origin
          })
        });
        
        if (!payRes.ok) {
          const payErr = await payRes.json().catch(() => ({}));
          toast.error(payErr.detail || 'Zahlung konnte nicht gestartet werden');
          return;
        }
        
        const payData = await payRes.json();
        if (payData.payment_url) {
          toast.success('Du wirst zur Zahlung weitergeleitet...');
          isRedirecting.current = true;
          window.location.href = payData.payment_url;
        } else {
          toast.error('Keine Zahlungs-URL erhalten');
        }
        return;
      }
      
      // Kein Artikel im Warenkorb
      toast.error('Warenkorb ist leer');
    } catch (e) {
      console.error('Payment error:', e);
      toast.error('Verbindungsfehler bei der Zahlung');
    }
  };

  // Progress indicator
  const getProgress = () => {
    switch (currentStep) {
      case 'cart': return 50;
      case 'account': return 100;
      default: return 0;
    }
  };

  // =====================
  // RENDER: Cart Step
  // =====================
  const renderCartStep = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Dein Warenkorb
          </CardTitle>
          <CardDescription>
            {itemCount} {itemCount === 1 ? 'Artikel' : 'Artikel'} im Warenkorb
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {itemCount === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg text-muted-foreground mb-4">Dein Warenkorb ist leer</p>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={() => navigate('/preise')}>
                  Cloud konfigurieren
                </Button>
                <Button variant="outline" onClick={() => navigate('/domains')}>
                  Domains suchen
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Cloud Item */}
              {cart.cloud && (
                <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Cloud className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold text-lg">Wolkenhelden Cloud</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {cart.cloud.storage && (
                            <Badge variant="secondary" className="text-xs">
                              <HardDrive className="h-3 w-3 mr-1" />{cart.cloud.storage} GB
                            </Badge>
                          )}
                          {cart.cloud.collabora_users > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              <Users className="h-3 w-3 mr-1" />{cart.cloud.collabora_users} Collabora
                            </Badge>
                          )}
                          {cart.cloud.antivirus && (
                            <Badge variant="secondary" className="text-xs">
                              <Shield className="h-3 w-3 mr-1" />Antivirus
                            </Badge>
                          )}
                          {cart.cloud.hp_backend && (
                            <Badge variant="secondary" className="text-xs">
                              <Zap className="h-3 w-3 mr-1" />Turbo
                            </Badge>
                          )}
                        </div>
                        {cart.cloud.domain && (
                          <p className="text-sm text-muted-foreground mt-2">
                            <Globe className="h-3 w-3 inline mr-1" />
                            {cart.cloud.domain.name}
                            {cart.cloud.domain.type === 'purchase' && cart.cloud.domain.price > 0 && (
                              <span className="ml-1">({formatPrice(cart.cloud.domain.price)}/Jahr)</span>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg">{formatPrice(cart.cloud.pricing?.monthly)}/Mo</p>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={removeCloud}
                        className="h-8 w-8 text-destructive mt-1"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Domain Items */}
              {cart.domains?.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    <Globe className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{item.domain}</p>
                      <p className="text-sm text-muted-foreground">
                        {item.type === 'transfer' ? 'Domain-Transfer' : 'Domain-Registrierung'}
                        {item.years && item.years > 1 && ` · ${item.years} Jahre`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-semibold">{formatPrice(item.price)}/Jahr</span>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => removeDomain(item.id)}
                      className="h-8 w-8 text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              
              <Separator />
              
              {/* Gutscheincode */}
              <div className="pt-2">
                <div className="flex items-center gap-2 mb-3">
                  <Ticket className="h-4 w-4 text-primary" />
                  <span className="font-medium text-sm">Gutscheincode</span>
                </div>
                
                {appliedCoupon ? (
                  <div className="flex items-center justify-between bg-green-50 dark:bg-green-950/30 rounded-lg p-3 border border-green-200 dark:border-green-800">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                      <div>
                        <p className="font-medium text-green-700 dark:text-green-400">{appliedCoupon.code}</p>
                        <p className="text-sm text-green-600 dark:text-green-500">
                          -{formatPrice(discountedTotals.discount)} Rabatt
                          {appliedCoupon.duration && appliedCoupon.duration !== 'once' && (
                            <span className="text-xs text-blue-600 dark:text-blue-400 block mt-1">
                              ({getDurationLabel(appliedCoupon)})
                            </span>
                          )}
                          {appliedCoupon.applies_to !== 'all' && (
                            <span className="text-xs text-amber-600 dark:text-amber-400 block mt-1">
                              ({appliedCoupon.applies_to === 'cloud_only' ? 'nur für Cloud' : 'nur für Domains'})
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={removeCoupon}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      placeholder="z.B. HELD20"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      className="uppercase"
                      data-testid="input-coupon-code"
                    />
                    <Button
                      onClick={validateCoupon}
                      disabled={couponLoading || !couponCode.trim()}
                      variant="secondary"
                      data-testid="button-validate-coupon"
                    >
                      {couponLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Einlösen'
                      )}
                    </Button>
                  </div>
                )}
              </div>
              
              <Separator />
              
              {/* Totals */}
              <div className="space-y-2 pt-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Zwischensumme</span>
                  <span>{formatPrice(totals.initialTotal)}</span>
                </div>
                {appliedCoupon && discountedTotals.discount > 0 && (
                  <div className="flex justify-between text-green-600 dark:text-green-400">
                    <span>Rabatt ({appliedCoupon.code})</span>
                    <span>-{formatPrice(discountedTotals.discount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg pt-2 border-t">
                  <span className="font-medium">Heute zu zahlen</span>
                  <span className="font-bold">{formatPrice(discountedTotals.finalTotal)}</span>
                </div>
                {totals.monthlyTotal > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Dann monatlich</span>
                    <span>{formatPrice(totals.monthlyTotal)}</span>
                  </div>
                )}
                {totals.yearlyDomains > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Domains jährlich</span>
                    <span>{formatPrice(totals.yearlyDomains)}</span>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
      
      {itemCount > 0 && (
        <div className="flex gap-4">
          <Button variant="outline" onClick={() => navigate(-1)} className="flex-1">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Weiter einkaufen
          </Button>
          <Button onClick={() => setCurrentStep('account')} className="flex-1">
            Zur Kasse
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );

  // =====================
  // RENDER: Account Step
  // =====================
  const renderAccountStep = () => (
    <div className="space-y-6">
      {/* Login/Register Toggle für nicht-eingeloggte */}
      {!isAuthenticated && (
        <Card>
          <CardHeader>
            <CardTitle>Hast du bereits ein Konto?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Button 
                variant={isLoginMode ? 'outline' : 'default'} 
                onClick={() => setIsLoginMode(false)}
                className="flex-1"
              >
                <User className="h-4 w-4 mr-2" />
                Neues Konto erstellen
              </Button>
              <Button 
                variant={isLoginMode ? 'default' : 'outline'} 
                onClick={() => setIsLoginMode(true)}
                className="flex-1"
              >
                <Lock className="h-4 w-4 mr-2" />
                Anmelden
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Login Form */}
      {!isAuthenticated && isLoginMode && (
        <Card>
          <CardHeader>
            <CardTitle>Anmelden</CardTitle>
            <CardDescription>Mit deinem bestehenden Konto anmelden</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="login-email">E-Mail</Label>
              <Input
                id="login-email"
                type="email"
                value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                placeholder="deinname@beispiel.de"
                data-testid="login-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="login-password">Passwort</Label>
              <Input
                id="login-password"
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                placeholder="Dein Passwort"
                data-testid="login-password"
              />
            </div>
            <Button 
              onClick={handleLogin} 
              disabled={processing}
              className="w-full"
              data-testid="login-submit"
            >
              {processing && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Anmelden
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Registration / Update Form */}
      {(isAuthenticated || !isLoginMode) && (
        <Card>
          <CardHeader>
            <CardTitle>
              {isAuthenticated ? 'Deine Angaben' : 'Konto erstellen'}
            </CardTitle>
            <CardDescription>
              {isAuthenticated 
                ? 'Prüfe und ergänze deine Rechnungsdaten'
                : 'Für die Rechnung und dein Benutzerkonto'
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Info wenn eingeloggt */}
            {isAuthenticated && existingCustomer && (
              <div className="rounded-lg bg-primary/10 border border-primary/20 p-4">
                <div className="flex items-center gap-2 text-primary">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="font-medium">Du bist angemeldet als {user?.email}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Deine Daten wurden geladen. Du kannst sie hier bei Bedarf anpassen.
                </p>
              </div>
            )}

            {loadingCustomer && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <span className="ml-2 text-muted-foreground">Lade deine Daten...</span>
              </div>
            )}

            {/* Name */}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="firstName">
                  <User className="h-3 w-3 inline mr-1" />
                  Vorname *
                </Label>
                <Input
                  id="firstName"
                  value={regForm.firstName}
                  onChange={(e) => setRegForm({ ...regForm, firstName: e.target.value })}
                  required
                  data-testid="input-firstname"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Nachname *</Label>
                <Input
                  id="lastName"
                  value={regForm.lastName}
                  onChange={(e) => setRegForm({ ...regForm, lastName: e.target.value })}
                  required
                  data-testid="input-lastname"
                />
              </div>
            </div>

            {/* Email - nur für neue Benutzer */}
            {!isAuthenticated && (
              <div className="space-y-2">
                <Label htmlFor="email">
                  <Mail className="h-3 w-3 inline mr-1" />
                  E-Mail *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={regForm.email}
                  onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                  placeholder="deinname@beispiel.de"
                  required
                  data-testid="input-email"
                />
              </div>
            )}

            {/* Password - nur für neue Benutzer */}
            {!isAuthenticated && (
              <div className="rounded-lg border p-4 space-y-4 bg-muted/30">
                <div className="flex items-center gap-2">
                  <Lock className="h-4 w-4 text-primary" />
                  <span className="font-medium">Passwort für dein Benutzerkonto</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Nach der Bestellung kannst du dich mit E-Mail und Passwort anmelden.
                </p>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="password">Passwort *</Label>
                    <Input
                      id="password"
                      type="password"
                      value={regForm.password}
                      onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                      placeholder="Mindestens 8 Zeichen"
                      minLength={8}
                      required
                      data-testid="input-password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="passwordConfirm">Passwort wiederholen *</Label>
                    <Input
                      id="passwordConfirm"
                      type="password"
                      value={regForm.passwordConfirm}
                      onChange={(e) => setRegForm({ ...regForm, passwordConfirm: e.target.value })}
                      placeholder="Passwort bestätigen"
                      required
                      data-testid="input-password-confirm"
                    />
                  </div>
                </div>
                {regForm.password && regForm.password.length < 8 && (
                  <p className="text-xs text-amber-600">
                    <AlertCircle className="h-3 w-3 inline mr-1" />
                    Mindestens 8 Zeichen erforderlich
                  </p>
                )}
                {regForm.password && regForm.passwordConfirm && regForm.password !== regForm.passwordConfirm && (
                  <p className="text-xs text-red-600">
                    <AlertCircle className="h-3 w-3 inline mr-1" />
                    Passwörter stimmen nicht überein
                  </p>
                )}
              </div>
            )}

            <Separator />
            <p className="text-sm font-medium flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Rechnungsadresse
            </p>

            {/* Company */}
            <div className="space-y-2">
              <Label htmlFor="company">
                <Building className="h-3 w-3 inline mr-1" />
                Firma (optional)
              </Label>
              <Input
                id="company"
                value={regForm.company}
                onChange={(e) => setRegForm({ ...regForm, company: e.target.value })}
                placeholder="Muster GmbH"
                data-testid="input-company"
              />
            </div>

            {/* Street */}
            <div className="space-y-2">
              <Label htmlFor="street">Straße & Hausnummer *</Label>
              <Input
                id="street"
                value={regForm.street}
                onChange={(e) => setRegForm({ ...regForm, street: e.target.value })}
                required
                data-testid="input-street"
              />
            </div>

            {/* PLZ & City */}
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="zipCode">PLZ *</Label>
                <Input
                  id="zipCode"
                  value={regForm.zipCode}
                  onChange={(e) => setRegForm({ ...regForm, zipCode: e.target.value })}
                  required
                  data-testid="input-zip"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="city">Stadt *</Label>
                <Input
                  id="city"
                  value={regForm.city}
                  onChange={(e) => setRegForm({ ...regForm, city: e.target.value })}
                  required
                  data-testid="input-city"
                />
              </div>
            </div>

            {/* Phone - PFLICHT bei Domains */}
            <div className="space-y-2">
              <Label htmlFor="phone">
                <Phone className="h-3 w-3 inline mr-1" />
                Telefonnummer {hasDomains ? '*' : '(optional)'}
              </Label>
              <Input
                id="phone"
                type="tel"
                value={regForm.phone}
                onChange={(e) => setRegForm({ ...regForm, phone: e.target.value })}
                placeholder="+49 123 4567890"
                required={hasDomains}
                data-testid="input-phone"
              />
              {hasDomains && (
                <p className="text-xs text-muted-foreground">
                  <AlertCircle className="h-3 w-3 inline mr-1" />
                  Erforderlich für die Domain-Registrierung (WHOIS-Daten)
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Legal Checkboxes */}
      {(isAuthenticated || !isLoginMode) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Scale className="h-5 w-5" />
              Rechtliche Vereinbarungen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* AGB Checkbox */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="agb"
                checked={agbAccepted}
                onCheckedChange={setAgbAccepted}
                data-testid="checkbox-agb"
              />
              <label htmlFor="agb" className="text-sm leading-relaxed cursor-pointer">
                Ich habe die{' '}
                <a 
                  href="https://wolkenhelden.de/agb" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary underline hover:text-primary/80 inline-flex items-center gap-1"
                >
                  Allgemeinen Geschäftsbedingungen (AGB)
                  <ExternalLink className="h-3 w-3" />
                </a>
                {' '}und die{' '}
                <a 
                  href="https://wolkenhelden.de/datenschutz" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary underline hover:text-primary/80 inline-flex items-center gap-1"
                >
                  Datenschutzerklärung
                  <ExternalLink className="h-3 w-3" />
                </a>
                {' '}gelesen und akzeptiere diese.
              </label>
            </div>

            {/* SLA Checkbox */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="sla"
                checked={slaAccepted}
                onCheckedChange={setSlaAccepted}
                data-testid="checkbox-sla"
              />
              <label htmlFor="sla" className="text-sm leading-relaxed cursor-pointer">
                Ich habe die{' '}
                <a 
                  href="https://wolkenhelden.de/sla" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary underline hover:text-primary/80 inline-flex items-center gap-1"
                >
                  Service Level Agreement (SLA)
                  <ExternalLink className="h-3 w-3" />
                </a>
                {' '}und die{' '}
                <a 
                  href="https://wolkenhelden.de/avv" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary underline hover:text-primary/80 inline-flex items-center gap-1"
                >
                  Auftragsverarbeitungsvereinbarung (AVV)
                  <ExternalLink className="h-3 w-3" />
                </a>
                {' '}gelesen und akzeptiere diese.
              </label>
            </div>

            {/* Warnung */}
            {(!agbAccepted || !slaAccepted) && (
              <div className="flex items-center gap-2 text-amber-600 dark:text-amber-500 text-sm pt-2">
                <AlertCircle className="h-4 w-4" />
                Bitte akzeptiere die AGB und SLA um fortzufahren
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Zahlungsmethode - vor Navigation */}
      {(isAuthenticated || !isLoginMode) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Zahlungsmethode & Einzugsermächtigung
            </CardTitle>
            <CardDescription>
              Wähle deine bevorzugte Zahlungsmethode. Die Zahlungsdaten werden für automatische Verlängerungen gespeichert.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Payment method selection */}
            <div className="grid gap-3 md:grid-cols-2">
              <button
                type="button"
                onClick={() => setPaymentMethod('stripe')}
                className={`p-4 rounded-lg border-2 transition-all text-left ${
                  paymentMethod === 'stripe'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
                data-testid="payment-method-stripe"
              >
                <div className="flex items-center gap-3">
                  <CreditCard className="h-6 w-6" />
                  <div>
                    <p className="font-medium">Kreditkarte / SEPA</p>
                    <p className="text-xs text-muted-foreground">Karte oder Lastschrift</p>
                  </div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setPaymentMethod('paypal')}
                className={`p-4 rounded-lg border-2 transition-all text-left ${
                  paymentMethod === 'paypal'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
                data-testid="payment-method-paypal"
              >
                <div className="flex items-center gap-3">
                  <div className="h-6 w-6 flex items-center justify-center font-bold text-blue-600">P</div>
                  <div>
                    <p className="font-medium">PayPal</p>
                    <p className="text-xs text-muted-foreground">Sicher bezahlen mit PayPal</p>
                  </div>
                </div>
              </button>
            </div>

            {/* Einzugsermächtigung Hinweis */}
            <div className="rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 p-4">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <Shield className="h-4 w-4 inline mr-2" />
                <strong>Einzugsermächtigung:</strong> Mit der Bestellung erteilst du uns die Erlaubnis, 
                {totals.monthlyTotal > 0 
                  ? ` monatlich ${formatPrice(totals.monthlyTotal)} für deine Cloud-Dienste`
                  : ''
                }
                {totals.monthlyTotal > 0 && totals.yearlyDomains > 0 ? ' und' : ''}
                {totals.yearlyDomains > 0 
                  ? ` jährlich ${formatPrice(totals.yearlyDomains)} für Domain-Verlängerungen`
                  : ''
                }
                {' '}automatisch abzubuchen. Du kannst jederzeit kündigen.
              </p>
            </div>

            {/* Rabatt Anzeige wenn vorhanden */}
            {appliedCoupon && discountedTotals.discount > 0 && (
              <div className="flex justify-between items-center text-green-600 dark:text-green-400 text-sm">
                <span>Rabatt ({appliedCoupon.code})</span>
                <span>-{formatPrice(discountedTotals.discount)}</span>
              </div>
            )}

            {/* Heute zu zahlen */}
            <div className="flex justify-between items-center pt-2 border-t">
              <span className="font-medium">Heute zu zahlen:</span>
              <span className="text-xl font-bold">{formatPrice(discountedTotals.finalTotal)}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Navigation */}
      {(isAuthenticated || !isLoginMode) && (
        <div className="flex gap-4">
          <Button variant="outline" onClick={() => setCurrentStep('cart')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück
          </Button>
          <Button 
            onClick={isAuthenticated ? handleUpdateAndProceed : handleRegisterAndProceed}
            disabled={processing || !canProceedFromAccount() || !agbAccepted || !slaAccepted}
            className="flex-1 bg-gradient-to-r from-primary to-blue-600"
            data-testid="proceed-to-payment"
          >
            {processing ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-2" />Wird verarbeitet...</>
            ) : (
              <>
                <CreditCard className="h-4 w-4 mr-2" />
                Kostenpflichtig bestellen
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="container mx-auto px-4 max-w-2xl">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-sm mb-2">
            <span className={currentStep === 'cart' ? 'font-medium text-primary' : 'text-muted-foreground'}>
              Warenkorb
            </span>
            <span className={currentStep === 'account' ? 'font-medium text-primary' : 'text-muted-foreground'}>
              Kasse & Zahlung
            </span>
          </div>
          <Progress value={getProgress()} className="h-2" />
        </div>

        {/* Content */}
        {currentStep === 'cart' && renderCartStep()}
        {currentStep === 'account' && renderAccountStep()}
      </div>
    </div>
  );
};

export default CartCheckoutPage;
